import { Link } from "react-router-dom";
import { ROUTES } from "../../../routes";
import { Dropdown } from "react-bootstrap";
import { useState, useEffect, useContext } from "react";
import ModulePopup from "../../../modules/popup/popup";
import usePageTitle from "../../../hooks/use-page-title";
import ApiWLFormat from "../../../modules/api/wl-format";
import Notifier from "../../../modules/notifier/notifier";
import Debouncer from "../../../modules/debouncer/debouncer";
import FGCheckbox from "../../../components/dumb/FGCheckbox";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import {
  MdSearch,
  MdDelete,
  MdContentCopy,
  MdModeEditOutline,
} from "react-icons/md";
import ServerSideWLFormatDataTable from "./../../../components/common/ServerSideWLFormatDataTable";

export default function PageWLFormatList() {
  const title = "Workplace Learning Report Builder";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [includeArchives, setIncludeArchives] = useState(false);
  const [duplicatedWLFormat, setDuplicatedWLFormat] = useState<any>(null);
  const [selectedWLFormats, setSelectedWLFormats] = useState<any[]>([]);

  useEffect(() => {
    setCustomText(title);
  } , []);

  useEffect(() => {
    if (!duplicatedWLFormat) return;
    ModulePopup.showAlertPrompt({
      title: "Duplicate Report",
      description: "Name:",
      input: `Copy of ${duplicatedWLFormat.name}`,
      onYes: (input) => {
        let name = input?.trim();
        if (!name) {
          Notifier.error("Please specify name");
          return;
        }
        ApiWLFormat.duplicate(duplicatedWLFormat._id, name)
          .then((res) => {
            if (res.result === "OK") {
              setRefresh(!refresh);
              Notifier.success(
                "Your Workplace Learning Report has been duplicated."
              );
            } else {
              Notifier.error(res.message);
            }
          })
          .catch((error) => Notifier.error(error.message));
        setDuplicatedWLFormat(null);
      },
      onNo: () => setDuplicatedWLFormat(null),
    });
  }, [duplicatedWLFormat]);

  return (
    <main className="form-generic content-generic content-generic--fluid">
      {/* <h1 className="fs-title hide-mobile-down">{title}</h1> */}

      <div className="cg__actions">
        <div className="cg__action-buttons">
          <Link
            to={`${ROUTES.PORTAL_WL_FORMATS}/add`}
            className="btn btn-secondary"
          >
            Add New
          </Link>
          <button
            className="btn btn-outline-dark"
            onClick={() => setRefresh((i) => !i)}
          >
            Refresh
          </button>{" "}
          {selectedWLFormats.length > 0 && (
            <button
              className="btn btn-outline-primary"
              onClick={(e) => {
                const deletedIds = selectedWLFormats
                  .filter((i) => typeof i._id !== "undefined")
                  .map((i) => i._id);
                ModulePopup.showAlertConfirm({
                  title: "Delete Confirmation",
                  description: (
                    <p>
                      Are you sure to delete {deletedIds.length} formats?
                      <br /> This action can't be undone.
                    </p>
                  ),
                  onYes: () =>
                    Debouncer.execute("DELETE_WL_FORMATS", () => {
                      ApiWLFormat.deleteByIds(deletedIds)
                        .then((res) => {
                          if (res.result === "OK") {
                            setRefresh((i) => !i);
                            setSelectedWLFormats([]);
                            Notifier.success(res.message);
                          } else {
                            Notifier.error(res.message);
                          }
                        })
                        .catch((error) => {
                          Notifier.error(error.message);
                        });
                    }),
                });
              }}
            >
              Delete Selected
            </button>
          )}
        </div>
        <div
          className="d-flex flex-column-reverse flex-wrap left-flex-mobile"
          style={{ gap: 10 }}
        >
          <div className="d-flex align-items-center" style={{ gap: 7 }}>
            <div style={{ paddingTop: 2 }}>
              <FGCheckbox
                checked={includeArchives}
                onChange={(e) => setIncludeArchives(e.target.checked)}
              />
            </div>
            <span
              style={{ cursor: "pointer" }}
              onClick={() => setIncludeArchives(!includeArchives)}
            >
              Include Archives
            </span>
          </div>
          <div className="cg__action-search">
            <span>
              <MdSearch />
            </span>
            <input
              type="text"
              placeholder="Search"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <ServerSideWLFormatDataTable
        api={ApiWLFormat.pagingSearch}
        search={search}
        includeArchives={includeArchives}
        onSelectedRowsChange={({ selectedRows }) =>
          setSelectedWLFormats(selectedRows)
        }
        columns={[
          {
            id: "name",
            name: "Name of Report",
            selector: (row) => row.name,
            // selector: (row) => (
            //   <Link className="title-column" to={`edit/${row._id}`}>
            //     {row.name}
            //   </Link>
            // ),
            sortable: true,
            maxWidth: "400px",
          },
          {
            id: "description",
            name: "Description of Report",
            selector: (row) => row.description,
            sortable: true,
            maxWidth: "400px",
          },
          {
            id: "programme",
            name: "Programme",
            selector: (row) => row.programmeName,
            sortable: true,
            maxWidth: "150px",
          },
          {
            id: "modifiedUser",
            name: "Updated By",
            selector: (row) => row.modifiedUser,
            sortable: true,
          },
          {
            id: "modifiedDate",
            name: "Date Modified",
            maxWidth: "200px",
            selector: (row) =>
              row.modifiedDate
                ? new Date(row.modifiedDate).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : null,
            sortable: true,
          },
          {
            id: "action",
            name: "",
            button: true,
            cell: (row) => (
              <div>
                <Dropdown align={"end"} drop={"down"}>
                  <Dropdown.Toggle
                    variant="secondary"
                    id="dropdown-basic"
                    className="btn-sm"
                  >
                    Manage
                  </Dropdown.Toggle>

                  <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                    <Link className="dropdown-item" to={`edit/${row._id}`}>
                      <MdModeEditOutline className="cursor-pointer icon-action-default" /> Edit
                    </Link>

                    <Dropdown.Divider />

                    <button
                      className="dropdown-item"
                      onClick={() => setDuplicatedWLFormat(row)}
                    >
                      <MdContentCopy /> Duplicate
                    </button>

                    <Dropdown.Divider />

                    <button
                      className="dropdown-item btn-danger"
                      onClick={() => {
                        ModulePopup.showAlertConfirm({
                          title: "Delete Confirmation",
                          description: (
                            <>
                              <p>
                                Are you sure to delete this format (
                                <strong>{row.name}</strong>)?
                                <br /> This action can't be undone.
                              </p>
                            </>
                          ),
                          onYes: () =>
                            Debouncer.execute("DELETE_WL_FORMAT", () =>
                              ApiWLFormat.deleteById(row._id)
                                .then((res) => {
                                  if (res.result === "OK") {
                                    setRefresh((i) => !i);
                                    Notifier.success(res.message);
                                  } else {
                                    Notifier.error(res.message);
                                  }
                                })
                                .catch((error) => Notifier.error(error.message))
                            ),
                        });
                      }}
                    >
                      <MdDelete /> Delete
                    </button>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ),
          },
        ]}
        refresh={refresh}
      />
    </main>
  );
}
